import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  setDoc,
  deleteDoc,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import type { Category } from '../../types'
import { dispatchCatalogEvent, MENU_UPDATED_EVENT } from '../catalogEvents'
import { getFirestoreInstance } from '../firebase'
import { getCurrentPlaceId } from '../placeConfig'
import { sortCategoriesByIndex } from '../menuSortOrder'
import {
  DEFAULT_TABLE_AREA_COLOR,
  pickNextTableAreaColor,
  UNASSIGNED_TABLE_AREA_COLOR,
} from '../tableAreaStorage'
import { commitWriteOps, type BatchWriteOp } from './writeBatches'

export const UNCATEGORIZED_CATEGORY_ID = '__uncategorized__'
export const UNCATEGORIZED_NAME = 'Uncategorized'

const uncategorizedCategory: Category = {
  id: UNCATEGORIZED_CATEGORY_ID,
  name: UNCATEGORIZED_NAME,
  menuKey: 'uncategorized',
  color: UNASSIGNED_TABLE_AREA_COLOR,
  sortIndex: 0,
}

const categoriesCacheByPlace: Record<string, Category[]> = {}
let categoriesUnsubscribe: (() => void) | null = null

function dispatchMenuUpdated(): void {
  dispatchCatalogEvent(MENU_UPDATED_EVENT)
}

function applyCategoriesCache(placeId: string, list: Category[]): void {
  categoriesCacheByPlace[placeId] = sortCategoriesByIndex(list)
}

function categoriesRef(placeId: string) {
  return collection(getFirestoreInstance(), 'places', placeId, 'categories')
}

/** Firestore reserves doc IDs starting with __; uncategorized stored as 'uncategorized'. */
export function categoryFirestoreDocId(categoryId: string): string {
  return categoryId === UNCATEGORIZED_CATEGORY_ID ? 'uncategorized' : categoryId
}

function categoryDoc(placeId: string, categoryId: string) {
  return doc(
    getFirestoreInstance(),
    'places',
    placeId,
    'categories',
    categoryFirestoreDocId(categoryId),
  )
}

function docToCategory(d: QueryDocumentSnapshot): Category {
  const data = d.data()
  const id =
    d.id === 'uncategorized' ? UNCATEGORIZED_CATEGORY_ID : d.id
  const sortIndex =
    typeof data.sortIndex === 'number' && Number.isFinite(data.sortIndex)
      ? data.sortIndex
      : undefined
  const color =
    typeof data.color === 'string' && data.color.trim()
      ? data.color.trim()
      : id === UNCATEGORIZED_CATEGORY_ID
        ? UNASSIGNED_TABLE_AREA_COLOR
        : DEFAULT_TABLE_AREA_COLOR
  const station =
    typeof data.station === 'string' && data.station.trim()
      ? data.station.trim()
      : undefined
  return {
    id,
    name: (data.name as string) ?? '',
    menuKey: typeof data.menuKey === 'string' ? data.menuKey : undefined,
    sortIndex,
    color,
    station,
  }
}

function categoryToFirestore(c: Category): Record<string, unknown> {
  return {
    id: c.id,
    name: c.name,
    menuKey: c.menuKey ?? null,
    sortIndex: c.sortIndex ?? 0,
    color: c.color?.trim() || DEFAULT_TABLE_AREA_COLOR,
    station: c.station?.trim() || null,
  }
}

function mergeCategoryPatch(existing: Category, patch: Partial<Category>): Category {
  const next: Category = {
    ...existing,
    ...patch,
    color: patch.color?.trim() || existing.color,
  }
  if ('station' in patch) {
    const s = patch.station?.trim()
    next.station = s || undefined
  }
  return next
}

export async function primeCategoriesCache(): Promise<void> {
  const placeId = getCurrentPlaceId()
  if (categoriesUnsubscribe) {
    categoriesUnsubscribe()
    categoriesUnsubscribe = null
  }
  const snap = await getDocs(categoriesRef(placeId))
  let list = snap.docs.map((d) => docToCategory(d))
  const hasUncategorized = list.some((c) => c.id === UNCATEGORIZED_CATEGORY_ID)
  if (!hasUncategorized) {
    list = [uncategorizedCategory, ...list]
    await setDoc(
      categoryDoc(placeId, UNCATEGORIZED_CATEGORY_ID),
      {
        id: UNCATEGORIZED_CATEGORY_ID,
        name: UNCATEGORIZED_NAME,
        menuKey: 'uncategorized',
        color: UNASSIGNED_TABLE_AREA_COLOR,
        sortIndex: 0,
      },
    )
  }
  applyCategoriesCache(placeId, list)
  categoriesUnsubscribe = onSnapshot(categoriesRef(placeId), (snapshot) => {
    applyCategoriesCache(placeId, snapshot.docs.map((d) => docToCategory(d)))
    void import('./menuItems').then((m) => {
      m.resortMenuItemsCache()
      dispatchMenuUpdated()
    })
  })
}

export function getCategories(): Category[] {
  const placeId = getCurrentPlaceId()
  return categoriesCacheByPlace[placeId] ?? []
}

export function saveCategories(categories: Category[]): void {
  const placeId = getCurrentPlaceId()
  const withIndex = categories.map((c, i) => ({ ...c, sortIndex: i }))
  categoriesCacheByPlace[placeId] = withIndex
  dispatchMenuUpdated()
  ;(async () => {
    const snap = await getDocs(categoriesRef(placeId))
    const logicalIds = new Set(withIndex.map((c) => c.id))
    const ops: BatchWriteOp[] = []
    for (const d of snap.docs) {
      const logicalId =
        d.id === 'uncategorized' ? UNCATEGORIZED_CATEGORY_ID : d.id
      if (!logicalIds.has(logicalId)) ops.push({ type: 'delete', ref: d.ref })
    }
    for (const c of withIndex) {
      ops.push({ type: 'set', ref: categoryDoc(placeId, c.id), data: categoryToFirestore(c) })
    }
    await commitWriteOps(ops)
  })().catch((e) => console.error('Firestore saveCategories failed', e))
}

function generateCategoryId(): string {
  return `C-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export function addCategory(category: Omit<Category, 'id'>): Category {
  const placeId = getCurrentPlaceId()
  const id = generateCategoryId()
  const cache = categoriesCacheByPlace[placeId] ?? []
  const maxIdx = cache.reduce((m, c) => Math.max(m, c.sortIndex ?? 0), -1)
  const nonDefaultCount = cache.filter((c) => c.id !== UNCATEGORIZED_CATEGORY_ID).length
  const full: Category = {
    ...category,
    id,
    sortIndex: maxIdx + 1,
    color: category.color?.trim() || pickNextTableAreaColor(nonDefaultCount),
  }
  categoriesCacheByPlace[placeId] = [...cache, full]
  dispatchMenuUpdated()
  setDoc(categoryDoc(placeId, id), categoryToFirestore(full)).catch((e) =>
    console.error('Firestore addCategory failed', e),
  )
  return full
}

export function updateCategory(id: string, patch: Partial<Category>): Category[] {
  const placeId = getCurrentPlaceId()
  const cache = categoriesCacheByPlace[placeId] ?? []
  categoriesCacheByPlace[placeId] = cache.map((c) =>
    c.id === id ? mergeCategoryPatch(c, patch) : c,
  )
  const found = categoriesCacheByPlace[placeId].find((c) => c.id === id)
  if (found) {
    setDoc(categoryDoc(placeId, id), categoryToFirestore(found)).catch((e) =>
      console.error('Firestore updateCategory failed', e),
    )
  }
  dispatchMenuUpdated()
  return categoriesCacheByPlace[placeId]
}

export function deleteCategory(id: string): Category[] {
  if (id === UNCATEGORIZED_CATEGORY_ID) return getCategories()
  const placeId = getCurrentPlaceId()
  const cache = categoriesCacheByPlace[placeId] ?? []
  categoriesCacheByPlace[placeId] = cache.filter((c) => c.id !== id)
  deleteDoc(categoryDoc(placeId, id)).catch((e) =>
    console.error('Firestore deleteCategory failed', e),
  )
  dispatchMenuUpdated()
  return categoriesCacheByPlace[placeId]
}

export function replaceStationNameInCategories(
  oldName: string,
  newName: string,
): void {
  const placeId = getCurrentPlaceId()
  const cache = categoriesCacheByPlace[placeId] ?? []
  const trimmed = newName.trim()
  if (!trimmed) return
  let changed = false
  const updated = cache.map((c) => {
    if (c.station !== oldName) return c
    changed = true
    return { ...c, station: trimmed }
  })
  if (changed) saveCategories(updated)
}

export function clearStationFromCategories(stationName: string): void {
  const placeId = getCurrentPlaceId()
  const cache = categoriesCacheByPlace[placeId] ?? []
  let changed = false
  const updated = cache.map((c) => {
    if (c.station !== stationName) return c
    changed = true
    const { station: _s, ...rest } = c
    return rest
  })
  if (changed) saveCategories(updated)
}

export function reorderCategories(orderedIds: string[]): void {
  const placeId = getCurrentPlaceId()
  const cache = categoriesCacheByPlace[placeId] ?? []
  const byId = new Map(cache.map((c) => [c.id, c]))
  const newOrder = orderedIds
    .map((id) => byId.get(id))
    .filter((c): c is Category => c != null)
  categoriesCacheByPlace[placeId] = newOrder
  saveCategories(newOrder)
}

export function isCategoriesPrimed(): boolean {
  const placeId = getCurrentPlaceId()
  return placeId in categoriesCacheByPlace
}

/** Clear in-memory cache and unsubscribe from real-time listener. */
export function clearCategoriesCache(): void {
  if (categoriesUnsubscribe) {
    categoriesUnsubscribe()
    categoriesUnsubscribe = null
  }
  Object.keys(categoriesCacheByPlace).forEach((k) => delete categoriesCacheByPlace[k])
}
